import React from 'react';
import { Modal } from 'react-native';
import {
  Box,
  Text,
  VStack,
  HStack,
  Heading,
  ScrollView,
  Button,
  ButtonIcon,
  FlatList,
} from '@gluestack-ui/themed';
import { X } from 'lucide-react-native';
import HtmlText from '@/common/components/HtmlText';
import type { Attachment } from '@/common/api/models/attachment';
import AttachmentItem from '@/student/task/components/Attachments/AttachmentItem';
import AttachmentMenu from '@/student/task/components/Attachments/AttachmentMenu';

export interface SubmissionDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  title: string;
  /** When provided, a "Task Description" section is shown above the submission. */
  description?: string;
  descriptionLabel?: string;
  submissionText?: string;
  submissionLabel: string;
  noSubmissionLabel?: string;
  /** Whether to apply student-submission HTML formatting (paragraphs, lists). */
  applySubmissionFormatting?: boolean;
  attachments?: Attachment[];
  attachmentsLabel?: string;
  noAttachmentsLabel?: string;
}

/**
 * SubmissionDetailsModal
 * ----
 * Fullscreen modal showing a student's submission, optionally with the
 * task description and attachments. Extracted from TaskFeedbackCard so
 * other grade-report cards (e.g. OralExamSection) can reuse the same UX.
 */
const SubmissionDetailsModal: React.FC<SubmissionDetailsModalProps> = ({
  visible,
  onClose,
  title,
  description,
  descriptionLabel = 'Task Description',
  submissionText,
  submissionLabel,
  noSubmissionLabel = 'No submission',
  applySubmissionFormatting = true,
  attachments,
  attachmentsLabel = 'ATTACHMENTS',
  noAttachmentsLabel = 'No attachments',
}) => (
  <Modal visible={visible} transparent>
    <Box flex={1} bg="rgba(0, 0, 0, 0.5)" justifyContent="center" alignItems="center">
      <Box bg="white" w="$3/4" maxWidth={1000} flex={1} m="$8" p="$0" borderRadius="$md" overflow="hidden">
        <Box bg="$backgroundLight100" p="$4" borderBottomWidth={1} borderBottomColor="$borderLight200">
          <HStack justifyContent="space-between" alignItems="center">
            <VStack flex={1} mr="$4">
              <Heading size="md">{title}</Heading>
            </VStack>
            <Button variant="link" onPress={onClose} p="$0">
              <ButtonIcon as={X} />
            </Button>
          </HStack>
        </Box>

        <ScrollView>
          <VStack p="$4" space="md">
            {!!description && (
              <Box bg="$white" p="$4" borderRadius="$md" borderWidth={1} borderColor="$borderLight200" mb="$4">
                <Heading size="sm" mb="$3">
                  {descriptionLabel}
                </Heading>
                <HtmlText
                  content={description}
                  numberOfLines={0}
                  fontSize={14}
                  contentStyle={{}}
                  applySubmissionFormatting={false}
                />
              </Box>
            )}

            <Box bg="$white" p="$4" borderRadius="$md" borderWidth={1} borderColor="$borderLight200">
              <Heading size="sm" mb="$3">
                {submissionLabel}
              </Heading>
              <HtmlText
                content={submissionText || noSubmissionLabel}
                numberOfLines={0}
                fontSize={14}
                contentStyle={{}}
                applySubmissionFormatting={applySubmissionFormatting}
              />

              {!!attachments && attachments.length > 0 && (
                <Box mt="$4">
                  <Text size="xs" color="$textSubtle" mb="$2">
                    {attachmentsLabel}
                  </Text>
                  <FlatList
                    data={attachments}
                    horizontal
                    keyExtractor={(item) => (item as Attachment).id}
                    renderItem={({ item }) => (
                      <AttachmentMenu
                        key={(item as Attachment).id}
                        attachment={item as Attachment}
                        allowDelete={false}
                        onDeleteAttachment={() => {}}
                        isDeleting={false}
                        trigger={({ ...triggerProps }) => (
                          <AttachmentItem
                            key={(item as Attachment).id}
                            attachment={item as Attachment}
                            triggerProps={triggerProps}
                            isDeleting={false}
                          />
                        )}
                      />
                    )}
                    ListEmptyComponent={() => (
                      <Text size="2xs" color="$textHint">
                        {noAttachmentsLabel}
                      </Text>
                    )}
                  />
                </Box>
              )}
            </Box>
          </VStack>
        </ScrollView>
      </Box>
    </Box>
  </Modal>
);

export default SubmissionDetailsModal;
